import { useT } from '../lib/i18n';

interface Props {
  onOpen: () => void;
}

/**
 * A button, where a QR code used to be printed on the board.
 *
 * The code was on show because a phone is how most players would rather score
 * and a feature nobody finds is a feature nobody has. But it was a permanent
 * white square in the middle of a dark board — the brightest object on a
 * screen otherwise built to keep the eye on two numbers — and it was there for
 * the whole match to serve an action taken once, in the first ten seconds, by
 * one person.
 *
 * So the offer stays and the code goes behind it. The words say what scanning
 * would get you, which the code never did: an unlabelled QR on a scoreboard is
 * a mystery most people will not scan to solve. Tapping opens the panel that
 * already existed for the cases a camera cannot solve, with the code, the
 * plain link and the address picker for a machine with more than one.
 */
export function RemoteButton({ onOpen }: Props) {
  const t = useT();

  return (
    <button
      type="button"
      onClick={onOpen}
      title={t('remote.hint')}
      className="hud-frame hud-frame-sm flex min-h-11 w-full shrink-0 items-center justify-center gap-2
                 px-3 py-2 transition hover:brightness-125 active:scale-[0.98]"
      style={{
        ['--hud-bright' as string]: 'rgba(255,255,255,0.28)',
        ['--hud-glow' as string]: 'transparent',
        ['--hud-fill' as string]: 'rgba(255,255,255,0.05)',
      }}
    >
      <PhoneIcon />
      <span className="hud-label text-center !text-white/80">{t('remote.useAsRemote')}</span>
    </button>
  );
}

/** A handset, because the word "remote" alone could mean the stream. */
function PhoneIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="h-4 w-4 shrink-0 text-white/80"
    >
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
      <path d="M10.5 5.5h3" />
      <path d="M12 18.2v.1" />
    </svg>
  );
}
